package com.eladfinish.bible.contest.features.verseWidget

import com.eladfinish.bible.contest.platform.PlatformTarget
import com.eladfinish.bible.contest.platform.platformInfo
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.usecase.GetRemoteConfigValueUseCase

/**
 * The address of the picture that shows what a home-screen widget is ("מה זה ווידג'ט?" on the widget's
 * settings screen). It is a public file in Google Drive and can be replaced without a release: either by
 * changing the file's content in Drive (same address) or by pointing [RemoteConfig.VERSE_WIDGET_EXAMPLE_IMAGE_URL]
 * to another one. The parameter is an address, not a secret.
 *
 * It is one parameter for both phones: the Firebase console gives it a value per platform (a condition on
 * App > Platform), because an Android home screen and an iPhone's look different. Without a fetched value
 * each platform shows its own built-in picture, see [defaultVerseWidgetExampleImageUrl].
 */
class GetVerseWidgetExampleImageUrlUseCase(
    private val getRemoteConfigValueUseCase: GetRemoteConfigValueUseCase,
    private val platform: PlatformTarget = platformInfo,
) {

    operator fun invoke(): String = verseWidgetExampleImageUrl(
        remoteUrl = runCatching {
            getRemoteConfigValueUseCase.get<String>(RemoteConfig.VERSE_WIDGET_EXAMPLE_IMAGE_URL)
        }.getOrNull(),
        platform = platform,
    )
}

/** The remote address is used only when it is a plain `https://` one, so a typo in the console cannot break the dialog. */
internal fun verseWidgetExampleImageUrl(remoteUrl: String?, platform: PlatformTarget): String {
    val remote = remoteUrl?.trim().orEmpty()
    val isValid = remote.startsWith("https://") && remote.length > "https://".length && remote.none { it.isWhitespace() }
    return if (isValid) remote else defaultVerseWidgetExampleImageUrl(platform)
}

/** The widget has a screen only on Android and iOS; the others never ask, and get the Android picture. */
internal fun defaultVerseWidgetExampleImageUrl(platform: PlatformTarget): String = when (platform) {
    PlatformTarget.IOS -> DEFAULT_VERSE_WIDGET_EXAMPLE_IMAGE_URL_IOS
    PlatformTarget.ANDROID,
    PlatformTarget.WEB,
    PlatformTarget.DESKTOP,
    -> DEFAULT_VERSE_WIDGET_EXAMPLE_IMAGE_URL_ANDROID
}

internal const val DEFAULT_VERSE_WIDGET_EXAMPLE_IMAGE_URL_ANDROID =
    "https://drive.google.com/uc?export=view&id=1mtN3XWIG_dtT1ImUMmhjcTWA2r0CfbCG"

internal const val DEFAULT_VERSE_WIDGET_EXAMPLE_IMAGE_URL_IOS =
    "https://drive.google.com/uc?export=view&id=1rIAPjop92XpNovQRNkT2Gt9s8ciJpgM6"
